import API_BASE from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Cart = ({ isOpen, onClose, onCheckoutSuccess }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');

  // Checkout form state
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bKash');
  const [trxId, setTrxId] = useState('');
  const [bkashNumber, setBkashNumber] = useState('');
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('participant_access_token');
  const participantUser = (() => { try { return JSON.parse(localStorage.getItem('participant_user') || 'null'); } catch { return null; } })();

  // Pre-fill from participant profile
  useEffect(() => {
    if (participantUser) {
      setEmail(participantUser.email || '');
      const name = [participantUser.first_name, participantUser.last_name].filter(Boolean).join(' ');
      setLeadName(name);
    }
  }, [isOpen]);

  const fetchCart = () => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API_BASE}/api/cart/list_items/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => { setItems(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) { fetchCart(); setShowCheckoutForm(false); setError(''); }
  }, [isOpen]);

  const handleRemove = async (itemId) => {
    try {
      await axios.delete(`${API_BASE}/api/cart/${itemId}/remove/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch {
      setError('Failed to remove item.');
    }
  };

  const total = items.reduce((sum, i) => sum + parseFloat(i.segment_fee || 0), 0);
  const paymentNumbers = items[0]?.payment_numbers || {};
  const paymentSystem = items[0]?.payment_system || 'manual';
  const activeSendNumber = paymentNumbers[paymentMethod] || 'No Number Assigned';

  const handleCopy = () => {
    navigator.clipboard.writeText(activeSendNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Validate all items are from same event
  const eventIds = [...new Set(items.map(i => i.event_id))];
  const mixedEvents = eventIds.length > 1;

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    if (mixedEvents) {
      setError('All cart items must be from the same event to checkout together.');
      return;
    }
    setCheckingOut(true);
    try {
      await axios.post(`${API_BASE}/api/cart/checkout/`, {
        lead_name: leadName,
        email,
        phone,
        payment_method: paymentSystem === 'gateway' ? 'Gateway' : paymentMethod,
        trx_id: paymentSystem === 'gateway' ? `GTW-${Date.now()}` : trxId,
        bkash_number: paymentSystem === 'gateway' ? 'GATEWAY' : bkashNumber,
        paid_amount: total,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems([]);
      setShowCheckoutForm(false);
      onCheckoutSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Checkout failed. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-[#080808] border-l border-white/[0.07] flex flex-col animate-in slide-in-from-right duration-300 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06] shrink-0">
          <div>
            <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em]">Registration Cart</p>
            <h2 className="text-xl font-black text-white uppercase tracking-tight">
              Cart{items.length > 0 && <span className="ml-2 text-sky-400">({items.length})</span>}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white text-xl font-bold transition-colors rounded-lg hover:bg-white/5"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {!token ? (
            // Not logged in
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-white/[0.03] border border-white/[0.07] rounded-3xl flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p className="text-gray-500 font-black uppercase tracking-widest text-xs mb-2">Sign In Required</p>
              <p className="text-gray-700 text-xs mb-6">Create an account or sign in to use the cart and track your registrations.</p>
              <button
                onClick={() => { onClose(); navigate('/participant/login'); }}
                className="bg-sky-500 text-black font-black px-6 py-3 rounded-xl uppercase text-[10px] tracking-widest hover:bg-sky-400 transition-all"
              >
                Sign In / Register
              </button>
            </div>
          ) : loading ? (
            <div className="p-8 text-center">
              <div className="text-sky-500 font-black text-xs uppercase tracking-widest animate-pulse">Loading...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-white/[0.03] border border-white/[0.07] rounded-3xl flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-gray-600 font-black uppercase tracking-widest text-[10px] mb-2">Cart is Empty</p>
              <p className="text-gray-700 text-xs">Browse events and add segments to your cart.</p>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              {/* Mixed event warning */}
              {mixedEvents && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                  <p className="text-amber-400 text-xs font-bold">
                    ⚠ Items from multiple events in cart. Remove items from other events to checkout.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  <p className="text-red-400 text-xs font-bold">{error}</p>
                </div>
              )}

              {/* Cart items */}
              {items.map(item => (
                <div key={item.id} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[9px] font-black text-sky-500 uppercase tracking-widest truncate">{item.event_name}</p>
                    <h4 className="text-sm font-black text-white uppercase tracking-tight mt-0.5 truncate">{item.segment_title}</h4>
                    <p className="text-base font-black text-white mt-1">{item.segment_fee} <span className="text-[10px] text-gray-500 font-bold">BDT</span></p>
                  </div>
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="shrink-0 text-red-500 hover:bg-red-500/10 w-8 h-8 flex items-center justify-center rounded-lg transition-all text-sm font-bold"
                  >
                    ✕
                  </button>
                </div>
              ))}

              {/* Total */}
              <div className="flex items-center justify-between px-4 py-3 bg-sky-500/5 border border-sky-500/10 rounded-xl">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total</span>
                <span className="text-lg font-black text-white">{total.toFixed(2)} <span className="text-[10px] text-gray-500">BDT</span></span>
              </div>

              {/* Checkout toggle */}
              {!showCheckoutForm && !mixedEvents && (
                <button
                  onClick={() => setShowCheckoutForm(true)}
                  className="w-full bg-sky-500 text-black font-black py-4 rounded-2xl uppercase text-[11px] tracking-[0.2em] hover:bg-sky-400 transition-all active:scale-95 shadow-lg shadow-sky-500/20"
                >
                  Proceed to Checkout →
                </button>
              )}

              {/* Checkout form */}
              {showCheckoutForm && !mixedEvents && (
                <form onSubmit={handleCheckout} className="space-y-4 border-t border-white/[0.06] pt-5 animate-in fade-in duration-300">
                  <p className="text-[10px] font-black text-sky-500 uppercase tracking-widest">Checkout Details</p>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Full Name *</label>
                    <input required value={leadName} onChange={e => setLeadName(e.target.value)} placeholder="Your full name"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-sky-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Email *</label>
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-sky-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 tracking-widest">Phone *</label>
                    <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="01XXXXXXXXX"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-sky-500 text-sm" />
                  </div>

                  {paymentSystem === 'manual' && (
                    <>
                      {/* Payment number display */}
                      <div className="bg-sky-500/5 border border-sky-500/20 p-3 rounded-xl flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[9px] font-black text-sky-500 uppercase tracking-widest">{paymentMethod} — Send {total.toFixed(2)} BDT</p>
                          <p className="text-white text-sm font-black truncate">{activeSendNumber}</p>
                        </div>
                        <button type="button" onClick={handleCopy}
                          className={`shrink-0 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-white text-black'}`}>
                          {copied ? '✓' : 'Copy'}
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1.5 tracking-widest">Method</label>
                          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none text-xs font-bold">
                            <option value="bKash">bKash</option>
                            <option value="Nagad">Nagad</option>
                            <option value="Rocket">Rocket</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1.5 tracking-widest">Sender</label>
                          <input type="tel" required value={bkashNumber} onChange={e => setBkashNumber(e.target.value)} placeholder="01XXX"
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none text-xs" />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1.5 tracking-widest">TrxID</label>
                          <input type="text" required value={trxId} onChange={e => setTrxId(e.target.value)} placeholder="TrxID"
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none font-mono text-xs" />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowCheckoutForm(false)}
                      className="flex-1 bg-white/5 text-gray-500 font-black py-3.5 rounded-xl uppercase text-[10px] border border-white/5 hover:text-white transition-colors">
                      Back
                    </button>
                    <button type="submit" disabled={checkingOut}
                      className="flex-1 bg-sky-500 text-black font-black py-3.5 rounded-xl uppercase text-[10px] tracking-widest hover:bg-sky-400 transition-all disabled:opacity-50 shadow-lg shadow-sky-500/20">
                      {checkingOut ? 'Processing...' : `Confirm (${total.toFixed(0)} BDT)`}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;